import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faSparkles,
  faExclamationTriangle,
  faSpinner,
  faCheckCircle,
} from '@fortawesome/pro-solid-svg-icons';

import Button from 'shared/button';
import {
  textMuted,
  aiImportContainer,
  aiImportHeader,
  aiImportTitle,
  aiImportDropzone,
  active,
} from './ai_document_import.module.scss';
import { joinWithAnd } from 'helpers/formatters/string_formats';
import { useListingDocumentUpload, ACCEPTED_TYPE_NAMES } from './use_listing_document_upload';

const ACCEPTED_TYPES_FOR_DISPLAY = joinWithAnd(ACCEPTED_TYPE_NAMES);

// Builds the AI ingest metadata object for submission to backend
export function buildIngestMetadata(processedData) {
  return {
    ai_extracted_fields: processedData.propertyFormData || {},
    inferred_fields: processedData.inferredFields || [],
    filename: processedData.uploadMetadata?.filename,
    upload_id: processedData.uploadMetadata?.uploadId,
    ai_model: processedData.uploadMetadata?.aiModel
  };
}

function AiDocumentImport({ saving, onProcessedData }) {
  const handleProcessedData = (processedData) => {
    const { uploadMetadata, ...dataToExpose } = processedData;
    const dataWithMetadata = {
      ...dataToExpose,
      ingestMetadata: buildIngestMetadata(processedData)
    };
    onProcessedData(dataWithMetadata);
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    status,
    fileDropError,
    uploadError,
    processingError,
    selectedFile,
    isUploading,
    progressText,
    resetUpload,
  } = useListingDocumentUpload(saving, handleProcessedData);

  const renderDropzoneContent = () => {
    if (isUploading) {
      return (
        <div className="text-center">
          <div className="text-primary mb-2">
            <FontAwesomeIcon icon={faSpinner} size="2x" spin />
          </div>
          <h5 className="text-center" style={{ fontSize: '15px' }}>
            {progressText}
          </h5>
          {selectedFile && (
            <div className={`${textMuted} text-center`}>{selectedFile.name}</div>
          )}
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="text-center">
          <div className="text-success mb-2">
            <FontAwesomeIcon icon={faCheckCircle} size="2x" />
          </div>
          <h5 className="text-center" style={{ fontSize: '15px' }}>
            Document processed successfully!
          </h5>
          {selectedFile && (
            <div className={`${textMuted} text-center`}>{selectedFile.name}</div>
          )}
        </div>
      );
    }

    if (fileDropError) {
      return (
        <Fragment>
          <div className="text-center">
            <div className="text-danger mb-2">
              <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
            </div>
            <h5 className="text-center text-danger" style={{ fontSize: '15px' }}>
              {fileDropError}
            </h5>
          </div>
          <div className={`${textMuted} text-center mt-3`}>We support {ACCEPTED_TYPES_FOR_DISPLAY}.</div>
        </Fragment>
      );
    }

    if (uploadError || processingError) {
      return (
        <div className="text-center">
          <div className="text-danger mb-2">
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
          </div>
          <h5 className="text-center text-danger" style={{ fontSize: '15px' }}>
            {(uploadError &&
               `Upload failed: ${uploadError.message || 'An error occurred during upload'}`) ||
             (processingError &&
               `Processing failed: ${processingError.error?.message ||
                 'An error occurred during processing'}`)}
          </h5>
          <Button.Secondary
            className="mt-3"
            onClick={(e) => {
              e.stopPropagation();
              resetUpload();
            }}
          >
            Try Again
          </Button.Secondary>
        </div>
      );
    }

    return (
      <Fragment>
        <div className="text-center text-primary">
          <FontAwesomeIcon icon={faUpload} size="2x" />
        </div>
        <h5 className="text-center" style={{ fontSize: '15px' }}>
          Drag or click to upload a listing document
        </h5>
        <div className={`${textMuted} text-center`}>We support {ACCEPTED_TYPES_FOR_DISPLAY}.</div>
      </Fragment>
    );
  };

  return (
    <div className={`py-4 my-4 ${aiImportContainer}`}>
      <div className={`d-flex align-items-start ${aiImportHeader}`}>
        <FontAwesomeIcon className="mr-2 mt-1" icon={faSparkles} />
        <div className="d-flex flex-column">
          <div className={aiImportTitle}>New! AI Document import</div>
          <div>Our AI will scan your document and automatically fill in key listing details for you.</div>
        </div>
      </div>
      <div
        className={`d-block py-4 ${aiImportDropzone} ${isDragActive ? active : ''}`}
        {...getRootProps()}
      >
        <input
          {...getInputProps({
            multiple: false
          })}
        />
        {renderDropzoneContent()}
      </div>
    </div>
  );
}

AiDocumentImport.propTypes = {
  saving: PropTypes.bool.isRequired,
  onProcessedData: PropTypes.func.isRequired,
};

export default React.memo(AiDocumentImport);
